import { useEffect, useRef, useState } from "react";

/* Reveal
   Adds a fade-up entrance the first time the element scrolls into view.
   Use sparingly on section headings, cards, and grids to give the site
   a calm, premium "settling-in" feel.

   Props:
   - as: element tag (default "div")
   - delay: ms delay before the animation starts (default 0)
   - stagger: if true, applies CSS variable `--rv-stagger` to children for staggered child reveal
   - once: animate only the first time (default true)
*/
const Reveal = ({
    as: Tag = "div",
    delay = 0,
    stagger = false,
    once = true,
    className = "",
    children,
    ...rest
}) => {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);

    // Sets up an IntersectionObserver on mount. The `once` prop controls
    // whether the observer disconnects after the first reveal. `ref.current`,
    // `el`, `entry`, `IntersectionObserver` are all local to the closure.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (typeof IntersectionObserver === "undefined") {
            setShown(true);
            return;
        }
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShown(true);
                    if (once) io.disconnect();
                } else if (!once) {
                    setShown(false);
                }
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [once]);

    const style = {
        transitionDelay: shown ? `${delay}ms` : "0ms",
    };

    return (
        <Tag
            ref={ref}
            style={style}
            className={`reveal ${shown ? "reveal-in" : ""} ${stagger ? "reveal-stagger" : ""} ${className}`}
            {...rest}
        >
            {children}
        </Tag>
    );
};

export default Reveal;
